import React from "react";
import { TooltipProps } from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import { IgTypography } from "@intelligems/ig-theme";
import { DataType, GraphConfig } from "@intelligems/ig-types";
import { Box, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import {
  getStandardMetricByAnalyticsMetricId,
  metricsConfig,
} from "@src/features/analytics/tables/analyticsAudienceTable/utils";

interface IgBarAnalyticsTooltipProps extends TooltipProps<ValueType, NameType> {
  config: GraphConfig;
  rawData: {
    variation_id: string;
    [metricId: string]: any;
  }[];
}

export const IgBarAnalyticsTooltip = (props: IgBarAnalyticsTooltipProps) => {
  const { active, payload, label, config, rawData } = props;
  const graphValueFormatter = useGraphValueFormatter();
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const variationId = payload[0]?.payload.variation_id;
  const data = rawData.find((data) => data.variation_id === variationId);

  const metricToUse = config.impactMetric ?? config.metrics[0];

  const numerator = getStandardMetricByAnalyticsMetricId(
    metricsConfig[metricToUse]?.numerator
  );
  const denominator = getStandardMetricByAnalyticsMetricId(
    metricsConfig[metricToUse]?.denominator
  );
  return (
    <Box
      sx={{
        display: "inline-block",
        padding: "10px",
        borderRadius: "5px",
        minWidth: "150px",
        maxWidth: "200px",
        backgroundColor: "rgba(73, 76, 80, 1)",
      }}
    >
      <Grid container direction={"column"} rowSpacing={1}>
        <Grid>
          <IgTypography
            color={"dark"}
            sx={{
              fontWeight: 600,
            }}
          >
            {label}
          </IgTypography>
        </Grid>

        {numerator && denominator ? (
          <Stack spacing={1} width="100%">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <IgTypography color={"dark"} sx={{ maxWidth: "100px" }}>
                {numerator?.name}
              </IgTypography>
              <IgTypography
                color={"dark"}
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  ml: 2,
                }}
              >
                {graphValueFormatter({
                  isAxisTick: false,
                  value: data?.[numerator.analyticsMetricId]?.value || 0,
                  configDataTypes: [numerator.analyticsDataType] as DataType[],
                  decimalAmountShown: 2,
                })}
              </IgTypography>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <IgTypography color={"dark"} sx={{ maxWidth: "100px" }}>
                {denominator?.name}
              </IgTypography>
              <IgTypography
                color={"dark"}
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  ml: 4,
                }}
              >
                {graphValueFormatter({
                  isAxisTick: false,
                  value: data?.[denominator.analyticsMetricId]?.value || 0,
                  configDataTypes: [
                    denominator.analyticsDataType,
                  ] as DataType[],
                  decimalAmountShown: 2,
                })}
              </IgTypography>
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <IgTypography color={"dark"} sx={{ maxWidth: "120px" }}>
              {config.title[0]}
            </IgTypography>
            <IgTypography
              color={"dark"}
              sx={{
                fontWeight: 600,
                whiteSpace: "nowrap",
                flexShrink: 0,
                ml: 2,
              }}
            >
              {graphValueFormatter({
                isAxisTick: false,
                value: Number(payload[0]?.value),
                configDataTypes: config.dataTypes,
                decimalAmountShown: 2,
              })}
            </IgTypography>
          </Stack>
        )}
      </Grid>
    </Box>
  );
};
